import { useEffect, useState } from 'react';
import { MILLISECONDS_TO_SECONDS } from '@/constants/time';
import { getVideoElementFromRef } from '@/utils/video-player';
import type { VideoPlayerEventEmitter } from '../VideoPlayerEventEmitter';

interface HlsMediaElement {
    api?: {
        latestLevelDetails?: {
            fragments?: { rawProgramDateTime?: string }[];
        };
    };
}

interface UseManifestBeginTimestampParams {
    videoRef: React.RefObject<HTMLVideoElement | null>;
    emitter: VideoPlayerEventEmitter;
    videoSrc: string | null;
}

export function useManifestBeginTimestamp({
    videoRef,
    emitter,
    videoSrc,
}: UseManifestBeginTimestampParams): number | null {
    const [manifest, setManifest] = useState<{
        videoSrc: string | null;
        beginTimestamp: number;
    } | null>(null);

    useEffect(() => {
        const readProgramDateTime = () => {
            const videoElement = getVideoElementFromRef(videoRef);
            if (!videoElement) return;

            const programDateTime = (videoElement as unknown as HlsMediaElement)
                .api?.latestLevelDetails?.fragments?.[0]?.rawProgramDateTime;
            if (!programDateTime) return;

            const parsedMilliseconds = new Date(programDateTime).getTime();
            if (Number.isNaN(parsedMilliseconds)) return;

            setManifest({
                videoSrc,
                beginTimestamp: parsedMilliseconds / MILLISECONDS_TO_SECONDS,
            });
        };

        const unsubscribeReady = emitter.on('ready', readProgramDateTime);
        const unsubscribeMetadata = emitter.on(
            'loadedmetadata',
            readProgramDateTime,
        );
        readProgramDateTime();

        return () => {
            unsubscribeReady();
            unsubscribeMetadata();
        };
    }, [emitter, videoRef, videoSrc]);

    return manifest?.videoSrc === videoSrc ? manifest.beginTimestamp : null;
}
